import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import jspPlatformApi, { addApiErrHandler } from "client/portals";
import { UserRead } from "client/jspPlatformExperiment";
import { useAppDispatch } from "hooks/useReduxStore";
import { userAccountActions } from "store/slices/user/userAccountSlice";
import { applyAccountLanguage } from "utils/language";

export interface AuthContextType {
  accessToken: string | undefined;
  user: UserRead | undefined;
  /** True while the user is being rehydrated after a refresh (token present,
   * user not yet fetched). Consumers can hold UI to avoid a flash. */
  isUserLoading: boolean;
  refreshUser: (user: UserRead) => void;
  signIn: (username: string, password: string, callback: VoidFunction) => void;
  signOut: (callback: VoidFunction) => void;
}

export const AuthContext = createContext<AuthContextType>(null!);

const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [accessToken, setAccessToken] = useState<string | undefined>(() => {
    const savedAccessToken = localStorage.getItem("accessToken");
    return savedAccessToken || undefined;
  });

  const [user, setUser] = useState<UserRead>();
  // Start in the loading state when a token is already present at startup, so
  // the very first paint after a refresh holds rather than flashes.
  const [isUserLoading, setIsUserLoading] = useState<boolean>(() =>
    Boolean(localStorage.getItem("accessToken")),
  );

  useEffect(() => {
    localStorage.setItem("accessToken", accessToken || "");
  }, [accessToken]);

  // Rehydrate the signed-in user after a page refresh: the token is restored
  // from localStorage but the user object is not, so re-fetch it (user_id 0 =
  // current user). An invalid/expired token surfaces as a 401, which the global
  // error handler turns into a sign-out.
  useEffect(() => {
    if (!accessToken || user) {
      setIsUserLoading(false);
      return;
    }
    setIsUserLoading(true);
    jspPlatformApi()
      .user.getUserAccountByUserId({ path: { user_id: 0 } })
      .then((res) => {
        if (res.data) {
          setUser(res.data);
          dispatch(userAccountActions.setUserAccount(res.data));
          applyAccountLanguage(res.data);
        }
      })
      .catch(() => {
        // 401 is handled by the global interceptor (sign-out); ignore others.
      })
      .finally(() => {
        setIsUserLoading(false);
      });
  }, [accessToken, user, dispatch]);

  const refreshUser = useCallback(
    (userData: UserRead) => {
      setUser(userData);
      dispatch(userAccountActions.setUserAccount(userData));
      applyAccountLanguage(userData);
    },
    [dispatch],
  );

  const signIn = useCallback(
    (username: string, password: string, callback: VoidFunction) => {
      jspPlatformApi()
        .authentication.postAuthToken({
          body: { username, password },
        })
        .then((res) => {
          const { data } = res;
          localStorage.setItem("accessToken", data?.access_token || "");
          setUser(data?.user);
          setAccessToken(data?.access_token);
          // Set user account in Redux store
          if (data?.user) {
            dispatch(userAccountActions.setUserAccount(data.user));
          }
          applyAccountLanguage(data?.user);
          callback();
        })
        .catch(() => {
          setAccessToken(undefined);
        });
    },
    [setAccessToken, dispatch],
  );

  const signOut = useCallback(
    (callback: VoidFunction) => {
      setAccessToken(undefined);
      setUser(undefined);
      // Reset user account in Redux store
      dispatch(userAccountActions.reset());
      callback();
    },
    [setAccessToken, dispatch],
  );

  useEffect(() => {
    addApiErrHandler((response): void => {
      switch (response.status) {
        case 401:
          signOut(() => {});
          break;
        default:
          break;
      }
    });
  });

  const authContext = useMemo(
    () => ({ accessToken, user, isUserLoading, refreshUser, signIn, signOut }),
    [accessToken, user, isUserLoading, refreshUser, signIn, signOut],
  );

  return (
    <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
